import { HashLineEdit } from '../types';

export interface HashLineAnchor {
  lineNumber: number;
  hash: string;
}

export interface HashLineResolvedEdit {
  startLine: number;
  endLine: number;
  original: string;
  suggested: string;
}

interface HashLineRange {
  startLine: number;
  endLine: number;
}

function findEndDocumentLine(lines: string[]): number | null {
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (lines[i].includes('\\end{document}')) {
      return i + 1;
    }
  }
  return null;
}

function assertEditStaysInsideDocument(lines: string[], startLine: number, endLine: number, op: HashLineEdit['op']) {
  const endDocumentLine = findEndDocumentLine(lines);
  if (!endDocumentLine) return;

  if (op === 'append') {
    throw new Error('AI edits cannot append content after \\end{document}. Re-run AI assist.');
  }

  if (startLine > endDocumentLine || endLine > endDocumentLine) {
    throw new Error('AI edits cannot modify content after \\end{document}. Re-run AI assist.');
  }
}

function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function toBase36Hash(value: number): string {
  return value.toString(36).toUpperCase().padStart(4, '0').slice(-4);
}

export function buildHashLine(lineContent: string, lineNumber: number): string {
  return toBase36Hash(fnv1a32(`${lineNumber}:${lineContent}`));
}

export function toHashLineContent(content: string): string {
  return content.split('\n').map((line, index) => (
    `${index + 1}#${buildHashLine(line, index + 1)}:${line}`
  )).join('\n');
}

export function parseHashLineAnchor(value: string): HashLineAnchor | null {
  const match = value.match(/^(\d+)#([A-Z0-9]+)$/);
  if (!match) return null;
  return {
    lineNumber: Number(match[1]),
    hash: match[2],
  };
}

function verifyAnchor(content: string, anchorValue: string): HashLineAnchor {
  const anchor = parseHashLineAnchor(anchorValue);
  if (!anchor) {
    throw new Error(`Invalid hashline anchor: ${anchorValue}`);
  }

  const lines = content.split('\n');
  const line = lines[anchor.lineNumber - 1];
  if (line === undefined) {
    throw new Error(`Hashline anchor ${anchorValue} points past the end of the file.`);
  }

  const actualHash = buildHashLine(line, anchor.lineNumber);
  if (actualHash !== anchor.hash) {
    throw new Error(
      `Hash mismatch for ${anchorValue}. The file changed after the AI read it. Re-run AI assist.`
    );
  }

  return anchor;
}

function resolveByAnchors(content: string, edit: HashLineEdit): HashLineRange {
  if (!edit.pos) {
    throw new Error(`Edit ${edit.id} is missing a starting anchor.`);
  }

  const start = verifyAnchor(content, edit.pos);
  const end = edit.end ? verifyAnchor(content, edit.end) : start;

  if (end.lineNumber < start.lineNumber) {
    throw new Error(`Edit ${edit.id} has an end anchor before its start anchor.`);
  }

  return {
    startLine: start.lineNumber,
    endLine: end.lineNumber,
  };
}

function findMatchingRanges(lines: string[], originalLines: string[]): HashLineRange[] {
  if (originalLines.length === 0 || originalLines.length > lines.length) {
    return [];
  }

  const matches: HashLineRange[] = [];
  const lastStart = lines.length - originalLines.length;

  for (let startIndex = 0; startIndex <= lastStart; startIndex += 1) {
    let isMatch = true;
    for (let offset = 0; offset < originalLines.length; offset += 1) {
      if (lines[startIndex + offset] !== originalLines[offset]) {
        isMatch = false;
        break;
      }
    }

    if (isMatch) {
      matches.push({
        startLine: startIndex + 1,
        endLine: startIndex + originalLines.length,
      });
    }
  }

  return matches;
}

function resolveByOriginalLines(lines: string[], edit: HashLineEdit): HashLineRange | null {
  if (edit.op !== 'replace' || !edit.originalLines || edit.originalLines.length === 0) {
    return null;
  }

  const matches = findMatchingRanges(lines, edit.originalLines);
  if (matches.length === 0) {
    return null;
  }

  const startAnchor = edit.pos ? parseHashLineAnchor(edit.pos) : null;
  const endAnchor = edit.end ? parseHashLineAnchor(edit.end) : startAnchor;

  if (startAnchor && endAnchor) {
    const exactRange = matches.find((range) => (
      range.startLine === startAnchor.lineNumber && range.endLine === endAnchor.lineNumber
    ));
    if (exactRange) {
      return exactRange;
    }

    const nearbyMatches = matches.filter((range) => (
      Math.abs(range.startLine - startAnchor.lineNumber) <= 3
      && Math.abs(range.endLine - endAnchor.lineNumber) <= 3
    ));
    if (nearbyMatches.length === 1) {
      return nearbyMatches[0];
    }
  }

  if (matches.length === 1) {
    return matches[0];
  }

  return null;
}

export function resolveHashLineEdit(content: string, edit: HashLineEdit): HashLineResolvedEdit {
  const lines = content.split('\n');

  if (edit.op === 'append') {
    assertEditStaysInsideDocument(lines, lines.length + 1, lines.length, edit.op);
    return {
      startLine: lines.length + 1,
      endLine: lines.length,
      original: '',
      suggested: edit.lines.join('\n'),
    };
  }

  if (edit.op === 'prepend') {
    assertEditStaysInsideDocument(lines, 1, 0, edit.op);
    return {
      startLine: 1,
      endLine: 0,
      original: '',
      suggested: edit.lines.join('\n'),
    };
  }

  let range: HashLineRange;
  try {
    range = resolveByAnchors(content, edit);
  } catch (error) {
    const fallbackRange = resolveByOriginalLines(lines, edit);
    if (!fallbackRange) {
      throw error;
    }
    range = fallbackRange;
  }

  assertEditStaysInsideDocument(lines, range.startLine, range.endLine, edit.op);

  return {
    startLine: range.startLine,
    endLine: range.endLine,
    original: lines.slice(range.startLine - 1, range.endLine).join('\n'),
    suggested: edit.lines.join('\n'),
  };
}

export function applyHashLineEdit(content: string, edit: HashLineEdit): string {
  const lines = content.split('\n');
  const replacement = edit.lines;

  if (edit.op === 'prepend') {
    return [...replacement, ...lines].join('\n');
  }

  if (edit.op === 'append') {
    return [...lines, ...replacement].join('\n');
  }

  const resolved = resolveHashLineEdit(content, edit);
  return [
    ...lines.slice(0, resolved.startLine - 1),
    ...replacement,
    ...lines.slice(resolved.endLine),
  ].join('\n');
}
